import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject, Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/accountDateFilter.model';
import { SummaryRequest } from '../../../../../_shared/models';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { MakeAndModelFilterComponent } from '../../../../_shared/components/filters/make-model-filter/make-model-filter.component';
import { MakeModelFilterData } from '../../../../_shared/models/make-model.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import {
  MultiLineChartComponent,
  MultiLineChartDataPoint,
  MultiLineChartSeries,
  LegendItem
} from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { transformValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance/maintenance-dashboard/models/maintenance-dashboard-response.model';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-work-orders-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    MultiLineChartComponent,
    EmptyStateComponent,
    GraphTrendIconComponent,
    AccountFilterComponent,
    MakeAndModelFilterComponent,
    DateFilterComponent
  ],
  templateUrl: './work-orders-modal.component.html',
  styleUrl: './work-orders-modal.component.scss'
})
export class WorkOrdersModalComponent implements OnInit {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);

  chartData: MultiLineChartDataPoint[] = [];
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly chartHeight = 335;

  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];

  readonly yAxisFormatter = (value: any): string => {
    return `${Number(value).toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  isLoading: boolean = true;
  title: string = 'maintenanceDashboard.charts.workOrders';
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();
  private modalFilter: WidgetFilterData = { ...this.filterService.filter };

  makeModelFilter: MakeModelFilterData = { makeModels: [] };
  accountIdsChanged$: ReplaySubject<number[]> = new ReplaySubject<number[]>(1);

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChartLabels());
  }

  private buildChartLabels(): void {
    const completedWorkOrders = this.transloco.translate('maintenanceDashboard.charts.completedWorkOrders');
    const globalTrend = this.transloco.translate('maintenanceDashboard.charts.globalTrend');

    this.series = [
      { key: 'globalTrend', color: '#EF4444', label: globalTrend, showDots: false },
      { key: 'completedWorkOrders', color: '#3B82F6', label: completedWorkOrders, showDots: true }
    ];
    this.legendItems = [
      { legend: completedWorkOrders, color: '#3B82F6' },
      { legend: globalTrend, color: '#EF4444' }
    ];
  }

  async ngOnInit(): Promise<void> {
    await this.loadWorkOrdersData();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.makeModelFilter = { makeModels: [] };
    this.accountIdsChanged$.next(filter.accountIds ?? []);
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.modalFilter = {
        ...this.modalFilter,
        ...this.dateFilter,
        ...this.accountFilter
      };

      await this.loadWorkOrdersData();
    }
  }

  onMakeAndModelChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.loadWorkOrdersData();
  }

  async loadWorkOrdersData(): Promise<void> {
    this.isLoading = true;

    try {
      const vehicleModelIds = this.makeModelFilter.makeModels
        ?.map(m => m.modelId)
        .filter(id => id > 0) ?? [];

      const request: AccountDateFilterRequest & { vehicleModelIds?: number[] } = {
        accountIds: this.modalFilter.accountIds,
        timeFrom: this.modalFilter.startDate!,
        timeTo: this.modalFilter.endDate!,
        interval: this.modalFilter.interval,
        vehicleModelIds: vehicleModelIds.length > 0 ? vehicleModelIds : undefined,
      };

      const summaryRequest: SummaryRequest & { vehicleModelIds?: number[] } = {
        accountIds: this.modalFilter.accountIds,
        timeFrom: this.modalFilter.startDate!,
        timeTo: this.modalFilter.endDate!,
        previousTimeFrom: this.modalFilter.previousCycleStartDate!,
        previousTimeTo: this.modalFilter.previousCycleEndDate!,
        vehicleModelIds: vehicleModelIds.length > 0 ? vehicleModelIds : undefined,
      };

      this.cancelSubject.next();

      const [completedResponse, globalResponse, statsResponse] = await Promise.all([
        this.dashboardService.getCompletedWorkOrdersRatio(request, this.cancelSubject),
        this.dashboardService.getGlobalCompletedWorkOrdersRatio(request, this.cancelSubject),
        this.dashboardService.getCompletedWorkOrdersStats(summaryRequest, this.cancelSubject)
      ]);

      this.totalValue = statsResponse?.data?.totalValue ?? 0;
      this.percentageChange = statsResponse?.data?.percentageChange ?? 0;

      this.chartData = this.processWorkOrdersData(
        completedResponse.data ?? [],
        globalResponse.data ?? []
      );

      this.calculateYTicks(this.chartData);
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
          this.totalValue = 0;
          this.percentageChange = 0;
        }
      });
    }
  }

  private processWorkOrdersData(
    completedData: MaintenanceInsightsResponse[],
    globalData: MaintenanceInsightsResponse[]
  ): MultiLineChartDataPoint[] {
    if (completedData.length === 0 && globalData.length === 0) return [];

    const daysDifference = differenceInDays(this.modalFilter.endDate!, this.modalFilter.startDate!);

    const datasets = [
      {
        label: 'completedWorkOrders',
        data: completedData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      },
      {
        label: 'globalTrend',
        data: globalData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      }
    ];

    let groupedData: any[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.modalFilter.startDate!, this.modalFilter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.modalFilter.startDate!, this.modalFilter.endDate!);
    }

    return groupedData.map(item => ({
      x: item.label,
      completedWorkOrders: Number(item['completedWorkOrders']) || 0,
      globalTrend: Number(item['globalTrend']) || 0
    }));
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues: number[] = [];

    data.forEach(point => {
      this.series.forEach(s => {
        const value = point[s.key];
        if (typeof value === 'number') {
          allValues.push(value);
        }
      });
    });

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true; // Work orders increase is generally positive
  }

  formatValue(): string {
    return transformValue(this.totalValue);
  }

  formatTooltipValue = (value: number): string => {
    return `${value.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  onExport(): void {
    // TODO: Implement export functionality
    console.log('Export clicked');
  }

  close(): void {
    this.activeModal.close();
  }
}